import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */

class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number | null} */
  #dragStartY = null;
  #dragCurrentY = 0;
  #dragHeight = 1;
  /** @type {AbortController | null} */
  #dragAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener('pointerdown', this.#handleDragStart);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener('pointerdown', this.#handleDragStart);
    this.#historyAbortController?.abort();
    this.#dragAbortController?.abort();
  }

  /**
   * Starts a swipe-to-dismiss gesture, but only from the drawer header/handle
   * (so scrolling the item list is unaffected).
   * @param {PointerEvent} event
   */
  #handleDragStart = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const dialog = this.refs.dialog;
    if (!dialog?.open) return;

    // Don't drag/close the cart drawer while a bottom-sheet popover
    // (Notes / Discount / Gifts & Shipping) is open on top of it.
    if (document.querySelector('.cart-actions-popover:popover-open')) return;

    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('.cart-drawer__header')) return;
    if (target.closest('.cart-drawer__close-button')) return;

    this.#dragStartY = event.clientY;
    this.#dragCurrentY = 0;
    this.#dragHeight = dialog.getBoundingClientRect().height || 1;
    dialog.style.transition = 'none';

    this.#dragAbortController = new AbortController();
    const { signal } = this.#dragAbortController;
    window.addEventListener('pointermove', this.#handleDragMove, { signal });
    window.addEventListener('pointerup', this.#handleDragEnd, { signal });
    window.addEventListener('pointercancel', this.#handleDragEnd, { signal });
  };

  /** @param {PointerEvent} event */
  #handleDragMove = (event) => {
    if (this.#dragStartY == null) return;

    this.#dragCurrentY = Math.max(0, event.clientY - this.#dragStartY);
    const dialog = this.refs.dialog;
    if (dialog) dialog.style.transform = `translateY(${this.#dragCurrentY}px)`;
  };

  #handleDragEnd = () => {
    const dialog = /** @type {HTMLDialogElement | undefined} */ (this.refs.dialog);
    const wasDragging = this.#dragStartY != null;

    this.#dragAbortController?.abort();
    this.#dragAbortController = null;
    this.#dragStartY = null;

    if (!wasDragging || !dialog) return;

    const dragged = this.#dragCurrentY;
    const threshold = Math.min(140, this.#dragHeight * 0.28);

    if (dragged > threshold) {
      // Smoothly finish the slide with a transform transition. closeDialog()
      // waits for it (via getAnimations), and we suppress the keyframe close
      // animation so the two don't fight — this makes a drag-close feel premium.
      dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      dialog.style.transition = 'transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)';
      dialog.style.transform = `translateY(${this.#dragHeight}px)`;
      this.closeDialog().finally(() => {
        dialog.style.transition = '';
        dialog.style.transform = '';
        dialog.style.removeProperty('--dialog-drawer-closing-animation');
        dialog.style.removeProperty('--start-y');
      });
    } else {
      // Snap back into place.
      dialog.style.transition = 'transform 0.32s cubic-bezier(0.22, 1, 0.36, 1)';
      dialog.style.transform = 'translateY(0)';
      const reset = () => {
        dialog.style.transition = '';
        dialog.style.transform = '';
      };
      dialog.addEventListener('transitionend', reset, { once: true });
      setTimeout(reset, 360);
    }
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
